/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: components/header/DashboardControls.tsx
 🧩 Description:
    Dashboard edit controls for the main header.
    Shows layout selector, add widget, and done buttons when editing.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
    LayoutGrid,
    Plus,
    Save,
    ChevronDown,
    Trash2,
    Check
} from 'lucide-react';
import { useDashboardEditStore } from '../../store/dashboardEdit';
import api from '../../lib/api';
import { useI18n } from '../../hooks/useI18n';

interface DashboardLayout {
    id: string;
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    widgets: any[];
}

interface DashboardConfig {
    layouts: DashboardLayout[];
    activeLayoutId: string | null;
}

// Event emitter for cross-component communication
export const dashboardEvents = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listeners: new Map<string, Set<(data?: any) => void>>(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    emit(event: string, data?: any) {
        this.listeners.get(event)?.forEach(fn => fn(data));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    on(event: string, fn: (data?: any) => void) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(fn);
        return () => this.listeners.get(event)?.delete(fn);
    }
};

export function DashboardControls() {
    const { t } = useI18n();
    const location = useLocation();
    const { isEditMode, setEditMode, setShowAddModal } = useDashboardEditStore();
    const [config, setConfig] = useState<DashboardConfig | null>(null);
    const [currentLayout, setCurrentLayout] = useState<DashboardLayout | null>(null);
    const [showLayoutMenu, setShowLayoutMenu] = useState(false);
    const [showNewLayoutInput, setShowNewLayoutInput] = useState(false);
    const [newLayoutName, setNewLayoutName] = useState('');
    const [loading, setLoading] = useState(false);

    // Only show on dashboard page
    const isDashboardPage = location.pathname === '/dashboard' || location.pathname === '/';
    const shouldShow = isDashboardPage && isEditMode;

    // Load dashboard config
    const loadConfig = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/admin/dashboard');

            if (response.data.success) {
                const dashConfig = response.data.data;
                setConfig(dashConfig);

                if (dashConfig.layouts.length > 0) {
                    const activeLayout = dashConfig.layouts.find((l: DashboardLayout) => l.id === dashConfig.activeLayoutId)
                        || dashConfig.layouts[0];
                    setCurrentLayout(activeLayout);
                }
            }
        } catch (err) {
            console.error('Failed to load dashboard config:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load on mount when in edit mode
    useEffect(() => {
        if (shouldShow) {
            loadConfig();
        }
    }, [shouldShow, loadConfig]);

    // Listen for external layout changes
    useEffect(() => {
        const handleRefresh = () => {
            loadConfig();
        };
        const unsub = dashboardEvents.on('refreshControls', handleRefresh);
        return unsub;
    }, [loadConfig]);

    // Handle create layout
    const handleCreateLayout = async () => {
        if (!newLayoutName.trim()) return;

        try {
            await api.post('/api/admin/dashboard/layouts', {
                name: newLayoutName.trim(),
                isDefault: false,
            });
            await loadConfig();
            setNewLayoutName('');
            setShowNewLayoutInput(false);
            setShowLayoutMenu(false);
            dashboardEvents.emit('layoutChanged');
        } catch (err) {
            console.error('Failed to create layout:', err);
        }
    };

    // Handle switch layout
    const handleSwitchLayout = async (layoutId: string) => {
        try {
            await api.put('/api/admin/dashboard/active-layout', { layoutId });

            if (config) {
                const layout = config.layouts.find(l => l.id === layoutId);
                if (layout) {
                    setCurrentLayout(layout);
                    setConfig({ ...config, activeLayoutId: layoutId });
                    dashboardEvents.emit('layoutChanged');
                }
            }
            setShowLayoutMenu(false);
        } catch (err) {
            console.error('Failed to switch layout:', err);
        }
    };

    // Handle delete layout
    const handleDeleteLayout = async () => {
        if (!currentLayout || (config?.layouts.length || 0) <= 1) return;

        try {
            await api.delete(`/api/admin/dashboard/layouts/${currentLayout.id}`);
            await loadConfig();
            dashboardEvents.emit('layoutChanged');
            setShowLayoutMenu(false);
        } catch (err) {
            console.error('Failed to delete layout:', err);
        }
    };

    // Handle done
    const handleDone = () => {
        // Request save from DashboardPage
        dashboardEvents.emit('saveLayout');

        // Short delay to allow save to start/complete? 
        // Or just exit mode and let saving happen in background.
        // DashboardPage will handle the saving.
        setEditMode(false);
    };

    // Handle add widget
    const handleAddWidget = () => {
        setShowAddModal(true);
    };

    // Don't render if not on dashboard page or not in edit mode
    if (!shouldShow) {
        return null;
    }

    return (
        <div className="flex items-center gap-3 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg mr-2">
            <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Editing</span>

            {/* Layout Selector */}
            <div className="relative">
                <button
                    onClick={() => setShowLayoutMenu(!showLayoutMenu)}
                    className="flex items-center gap-2 px-2 py-1 bg-card hover:bg-accent rounded text-sm font-medium text-foreground border border-border transition-colors"
                >
                    <LayoutGrid className="w-3.5 h-3.5" />
                    <span className="max-w-[100px] truncate">{currentLayout?.name || 'Layout'}</span>
                    <ChevronDown className="w-3.5 h-3.5" />
                </button>

                {showLayoutMenu && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowLayoutMenu(false)} />
                        <div className="absolute left-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-xl z-50 py-1">
                            {config?.layouts.map(layout => (
                                <button
                                    key={layout.id}
                                    onClick={() => handleSwitchLayout(layout.id)}
                                    className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-muted text-sm text-left"
                                >
                                    <span className="text-foreground truncate">{layout.name}</span>
                                    {layout.id === currentLayout?.id && (
                                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                                    )}
                                </button>
                            ))}

                            <div className="border-t border-border my-1" />

                            {showNewLayoutInput ? (
                                <div className="px-2 py-1.5 flex gap-1.5">
                                    <input
                                        type="text"
                                        value={newLayoutName}
                                        onChange={(e) => setNewLayoutName(e.target.value)}
                                        placeholder="Name..."
                                        className="flex-1 px-2 py-1 bg-background border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleCreateLayout();
                                            if (e.key === 'Escape') {
                                                setShowNewLayoutInput(false);
                                                setNewLayoutName('');
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={handleCreateLayout}
                                        className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs hover:bg-primary/90"
                                    >
                                        Add
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setShowNewLayoutInput(true)}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-muted text-sm text-primary"
                                >
                                    <Plus className="w-4 h-4" />
                                    New Layout
                                </button>
                            )}

                            {currentLayout && (config?.layouts.length || 0) > 1 && (
                                <button
                                    onClick={handleDeleteLayout}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-muted text-sm text-red-500"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Add Widget Button */}
            <button
                onClick={handleAddWidget}
                className="flex items-center gap-1.5 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors"
                title={t('dashboard.addWidget', 'Add Widget')}
            >
                <Plus className="w-3.5 h-3.5" />
                {t('dashboard.addWidget', 'Widget')}
            </button>

            {/* Done Button */}
            <button
                onClick={handleDone}
                className="flex items-center gap-1.5 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors"
                title={t('common.done', 'Done')}
            >
                <Save className="w-3.5 h-3.5" />
                {t('common.done', 'Done')}
            </button>
        </div>
    );
}
